/**
 * Lichte in-memory cache + preload voor trage pagina's (Klanten 360°, live bezoekers).
 * Stale-while-revalidate: toon cache meteen, vernieuw op de achtergrond.
 */
import api from './api';

const store = new Map();

function now() {
  return Date.now();
}

export function peekCache(key, maxAgeMs = Infinity) {
  const entry = store.get(key);
  if (!entry || entry.data === undefined) return null;
  if (now() - entry.at > maxAgeMs) return null;
  return entry.data;
}

export function setCache(key, data) {
  const prev = store.get(key) || {};
  store.set(key, { ...prev, data, at: now(), inflight: null });
  return data;
}

export function clearCacheKey(key) {
  store.delete(key);
}

export function getCachedOrFetch(key, fetcher, { maxAgeMs = 60_000, staleWhileRevalidate = true } = {}) {
  const entry = store.get(key);
  const age = entry?.at != null ? now() - entry.at : Infinity;
  const fresh = entry?.data !== undefined && age <= maxAgeMs;

  if (fresh) {
    return Promise.resolve({ data: entry.data, fromCache: true, stale: false });
  }

  if (staleWhileRevalidate && entry?.data !== undefined) {
    if (!entry.inflight) {
      const p = Promise.resolve()
        .then(fetcher)
        .then((data) => {
          setCache(key, data);
          return data;
        })
        .catch((err) => {
          const cur = store.get(key);
          if (cur) cur.inflight = null;
          throw err;
        });
      store.set(key, { ...entry, inflight: p });
    }
    return Promise.resolve({ data: entry.data, fromCache: true, stale: true });
  }

  if (entry?.inflight) {
    return entry.inflight.then((data) => ({ data, fromCache: false, stale: false }));
  }

  const p = Promise.resolve()
    .then(fetcher)
    .then((data) => {
      setCache(key, data);
      return data;
    })
    .catch((err) => {
      const cur = store.get(key);
      if (cur) cur.inflight = null;
      throw err;
    });
  store.set(key, { data: entry?.data, at: entry?.at || 0, inflight: p });
  return p.then((data) => ({ data, fromCache: false, stale: false }));
}

/** Start fetch op de achtergrond (nav-hover / idle warm). */
export function warmCache(key, fetcher, maxAgeMs = 60_000) {
  const entry = store.get(key);
  if (entry?.inflight) return entry.inflight;
  if (entry?.data !== undefined && now() - entry.at <= maxAgeMs * 0.7) {
    return Promise.resolve(entry.data);
  }
  const p = Promise.resolve()
    .then(fetcher)
    .then((data) => {
      setCache(key, data);
      return data;
    })
    .catch((err) => {
      const cur = store.get(key);
      if (cur) cur.inflight = null;
      throw err;
    });
  store.set(key, {
    data: entry?.data,
    at: entry?.at || 0,
    inflight: p,
  });
  return p;
}

export const CACHE_KEYS = {
  audienceWeek: (days = 7) => `audience-week:${days}`,
  live: (minutes = 5) => `attributie-live:${minutes}`,
  customer360: (email) => `customer-360:${String(email || '').trim().toLowerCase()}`,
};

const AUDIENCE_TTL = 90_000;
const LIVE_TTL = 12_000;
const PROFILE_TTL = 120_000;

export function prefetchAudienceWeek(days = 7) {
  return warmCache(
    CACHE_KEYS.audienceWeek(days),
    () => api.get(`/marketing/popup/audience-week?days=${days}`).then((r) => r.data),
    AUDIENCE_TTL
  );
}

export function prefetchAttributieLive(minutes = 5) {
  return warmCache(
    CACHE_KEYS.live(minutes),
    () => api.get(`/attributie/live?minutes=${minutes}`).then((r) => r.data),
    LIVE_TTL
  );
}

export function prefetchCustomer360(email) {
  const e = String(email || '').trim();
  if (!e.includes('@')) return Promise.resolve(null);
  return warmCache(
    CACHE_KEYS.customer360(e),
    () => api.get('/customers/360', { params: { email: e } }).then((r) => r.data),
    PROFILE_TTL
  );
}

/** Warm Klanten + Live na login / idle (niet blokkeren). */
export function warmSlowPages({ days = 7, liveMinutes = 5 } = {}) {
  const run = () => {
    prefetchAudienceWeek(days).catch(() => {});
    prefetchAttributieLive(liveMinutes).catch(() => {});
  };
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run, { timeout: 2500 });
  } else {
    setTimeout(run, 1200);
  }
}

export const TTL = { AUDIENCE_TTL, LIVE_TTL, PROFILE_TTL };
